import { useCallback, useEffect, useState } from 'react';
import { Power, RefreshCw, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  FinancialMigrationControl,
  FinancialMigrationMode,
  readFinancialMigrationControl,
  setFinancialMigrationControl,
} from '../../services/FinancialMigrationControlService';
import {
  FinancialReconciliationSnapshot,
  runFinancialReadReconciliation,
} from '../../services/FinancialMigrationReconciliationService';

const modeLabel: Record<FinancialMigrationControl['mode'], string> = {
  off: 'Desligado',
  read: 'Somente leitura',
  write: 'Leitura e gravação',
};

const statusLabel: Record<FinancialMigrationControl['status'], string> = {
  planned: 'Planejado',
  enabled: 'Habilitado',
  paused: 'Pausado',
  rolled_back: 'Revertido',
};

const A8_PILOT_CLIENT_ID = '88121e1e-6143-4bbd-a6b5-a0c663347e51';

export default function FinancialMigrationControlPage() {
  const { selectedClient } = useAuth();
  const [control, setControl] = useState<FinancialMigrationControl | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [savingMode, setSavingMode] = useState<FinancialMigrationMode | null>(null);
  const [notes, setNotes] = useState('');
  const [reconciliation, setReconciliation] = useState<FinancialReconciliationSnapshot | null>(null);
  const [reconciling, setReconciling] = useState(false);
  const [reconciliationError, setReconciliationError] = useState<string | null>(null);

  const loadControl = useCallback(async () => {
    if (!selectedClient?.clientId) return;
    setLoading(true);
    setError(null);
    setFeedback(null);
    try {
      setControl(await readFinancialMigrationControl(selectedClient.clientId));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Não foi possível consultar o controle de ativação.');
    } finally {
      setLoading(false);
    }
  }, [selectedClient?.clientId]);

  useEffect(() => { void loadControl(); }, [loadControl]);

  const updateControl = async (mode: FinancialMigrationMode, status: FinancialMigrationControl['status']) => {
    if (!selectedClient?.clientId || savingMode) return;

    if (mode === 'write' && selectedClient.clientId !== A8_PILOT_CLIENT_ID) {
      setError('A ativação write está restrita ao tenant piloto Revalidatie.');
      return;
    }
    if (mode === 'write' && !notes.trim()) {
      setError('Informe a observação e o escopo da janela antes de ativar a gravação controlada.');
      return;
    }

    const confirmation = mode === 'read'
      ? 'Ativar o piloto em modo somente leitura? Nenhuma gravação financeira será habilitada.'
      : mode === 'write'
        ? 'Ativar a gravação controlada somente para o piloto Revalidatie? Nenhum backfill ou migração será executado.'
        : 'Desligar o piloto A8 e retornar ao estado planejado?';
    if (!window.confirm(confirmation)) return;

    setSavingMode(mode);
    setError(null);
    setFeedback(null);
    try {
      const nextControl = await setFinancialMigrationControl({
        clientId: selectedClient.clientId,
        stage: 'a8',
        mode,
        status,
        notes: notes.trim() || null,
      });
      setControl(nextControl);
      setFeedback(mode === 'read'
        ? 'Piloto ativado em modo somente leitura. O modo write continua bloqueado.'
        : mode === 'write'
          ? 'Gravação controlada ativada somente para o piloto Revalidatie. Nenhum backfill ou migração foi executado.'
          : 'Piloto desligado e retornado ao estado planejado.');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Não foi possível alterar o controle de ativação.');
    } finally {
      setSavingMode(null);
    }
  };

  const runReconciliation = async () => {
    if (!selectedClient?.clientId || reconciling) return;
    setReconciling(true);
    setReconciliationError(null);
    try {
      setReconciliation(await runFinancialReadReconciliation(selectedClient.clientId));
    } catch (reconciliationCause) {
      setReconciliation(null);
      setReconciliationError(reconciliationCause instanceof Error
        ? reconciliationCause.message
        : 'Não foi possível executar a reconciliação somente leitura.');
    } finally {
      setReconciling(false);
    }
  };

  if (!selectedClient?.clientId) {
    return <div className="rounded-lg border bg-white p-6">Selecione um tenant para consultar o controle de ativação.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Controle de ativação financeira</h1>
          <p className="mt-1 text-sm text-gray-600">A8 · Migração e ativação gradual por tenant</p>
        </div>
        <button
          type="button"
          onClick={() => void loadControl()}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Atualizar estado
        </button>
      </div>

      {error && <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>}
      {feedback && <div role="status" className="rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-800">{feedback}</div>}

      <section className="rounded-lg border bg-white p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-6 w-6 text-green-700" />
          <div>
            <h2 className="font-semibold text-gray-900">Tenant piloto</h2>
            <p className="text-sm text-gray-600">{selectedClient.clientName} · {selectedClient.clientId}</p>
          </div>
        </div>

        {loading && !control ? (
          <p className="mt-6 text-sm text-gray-600">Consultando estado no banco...</p>
        ) : control ? (
          <>
            <dl className="mt-6 grid gap-4 sm:grid-cols-3">
              <div className="rounded-md bg-gray-50 p-4">
                <dt className="text-xs uppercase tracking-wide text-gray-500">Etapa</dt>
                <dd className="mt-1 font-semibold text-gray-900">{control.stage}</dd>
              </div>
              <div className="rounded-md bg-gray-50 p-4">
                <dt className="text-xs uppercase tracking-wide text-gray-500">Modo</dt>
                <dd className="mt-1 font-semibold text-gray-900">{modeLabel[control.mode]} ({control.mode})</dd>
              </div>
              <div className="rounded-md bg-gray-50 p-4">
                <dt className="text-xs uppercase tracking-wide text-gray-500">Status</dt>
                <dd className="mt-1 font-semibold text-gray-900">{statusLabel[control.status]} ({control.status})</dd>
              </div>
            </dl>

            <div className="mt-6 rounded-md border border-gray-200 bg-gray-50 p-4">
              <label htmlFor="a8-notes" className="block text-sm font-medium text-gray-700">Observação da alteração</label>
              <textarea
                id="a8-notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                rows={3}
                className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
                placeholder="Registre o motivo do teste ou da alteração."
                disabled={Boolean(savingMode)}
              />
              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => void updateControl('read', 'enabled')}
                  disabled={Boolean(savingMode) || (control.mode === 'read' && control.status === 'enabled')}
                  className="inline-flex items-center gap-2 rounded-md bg-blue-700 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Power className="h-4 w-4" />
                  {savingMode === 'read' ? 'Ativando...' : 'Ativar somente leitura'}
                </button>
                <button
                  type="button"
                  onClick={() => void updateControl('write', 'enabled')}
                  disabled={Boolean(savingMode) || selectedClient.clientId !== A8_PILOT_CLIENT_ID || (control.mode === 'write' && control.status === 'enabled')}
                  className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Power className="h-4 w-4" />
                  {savingMode === 'write' ? 'Ativando gravação...' : 'Ativar gravação controlada'}
                </button>
                <button
                  type="button"
                  onClick={() => void updateControl('off', 'planned')}
                  disabled={Boolean(savingMode) || (control.mode === 'off' && control.status === 'planned')}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Desligar piloto
                </button>
              </div>
              <p className="mt-3 text-xs text-gray-600">
                {control.mode === 'write' && control.status === 'enabled'
                  ? 'O modo write está ativo somente para o piloto Revalidatie. Backfill, migração e desativação do legado continuam fora desta ação.'
                  : 'A gravação controlada está restrita ao piloto Revalidatie e exige observação. Esta ação não executa backfill nem migração.'}
              </p>
            </div>

            <div className="mt-6 rounded-md border border-indigo-200 bg-indigo-50/60 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-gray-900">Reconciliação do piloto em modo read</h3>
                  <p className="mt-1 text-sm text-gray-700">Consulta o recorte atual de pacientes, contratos, pacotes, faturas, repasses e fluxo de caixa sem alterar dados.</p>
                </div>
                <button
                  type="button"
                  onClick={() => void runReconciliation()}
                  disabled={reconciling || control.mode !== 'read' || control.status !== 'enabled'}
                  className="inline-flex items-center gap-2 rounded-md bg-indigo-700 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {reconciling ? 'Executando...' : 'Executar reconciliação read-only'}
                </button>
              </div>
              {reconciliationError && <p role="alert" className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">{reconciliationError}</p>}
              {reconciliation && <div className="mt-4 space-y-4 rounded-md border border-indigo-200 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span><strong>Período:</strong> {reconciliation.periodFrom} a {reconciliation.periodUntil}</span>
                  <span className="font-medium text-green-700">Nenhuma escrita executada</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead><tr className="border-b text-xs uppercase text-gray-500"><th className="px-2 py-2">Recorte</th><th className="px-2 py-2 text-right">Total</th><th className="px-2 py-2 text-right">Amostra lida</th></tr></thead>
                    <tbody>{reconciliation.sections.map((item) => <tr key={item.section} className="border-b last:border-0"><td className="px-2 py-2">{{ patients: 'Pacientes', contracts: 'Contratos', packages: 'Pacotes', invoices: 'Faturas', settlements: 'Repasses' }[item.section]}</td><td className="px-2 py-2 text-right">{item.total}</td><td className="px-2 py-2 text-right">{item.sampleCount}</td></tr>)}</tbody>
                  </table>
                </div>
                <div>
                  <h4 className="mb-2 font-semibold text-gray-900">Comparação histórica read-only</h4>
                  <p className="mb-3 text-xs text-gray-600">Contagem na tabela legada versus contagem no modelo financeiro novo, sempre filtradas pelo tenant atual.</p>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                      <thead><tr className="border-b text-xs uppercase text-gray-500"><th className="px-2 py-2">Recorte</th><th className="px-2 py-2">Fonte legada</th><th className="px-2 py-2 text-right">Legado</th><th className="px-2 py-2 text-right">Novo mapeado</th><th className="px-2 py-2 text-right">Novo total</th><th className="px-2 py-2 text-right">Adicional</th><th className="px-2 py-2">Resultado</th></tr></thead>
                      <tbody>{reconciliation.historicalComparisons.map((item) => <tr key={item.key} className="border-b last:border-0"><td className="px-2 py-2">{item.label}</td><td className="px-2 py-2 font-mono text-xs">{item.legacyLabel}</td><td className="px-2 py-2 text-right">{item.legacyTotal}</td><td className="px-2 py-2 text-right">{item.mappedNewTotal}</td><td className="px-2 py-2 text-right">{item.newTotal}</td><td className="px-2 py-2 text-right">{item.newAdditionalTotal}</td><td className={`px-2 py-2 font-medium ${item.difference === 0 ? 'text-green-700' : 'text-amber-700'}`}><div>{item.difference === 0 ? 'Mapeamento coincidente' : 'Divergência para revisão'}</div>{item.note && <div className="mt-1 max-w-xs text-xs font-normal text-gray-600">{item.note}</div>}</td></tr>)}</tbody>
                    </table>
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-4">
                  <div className="rounded border bg-gray-50 p-3"><span className="text-xs text-gray-500">Saldo inicial</span><strong className="mt-1 block">R$ {reconciliation.cash.totals.openingBalance.toFixed(2).replace('.', ',')}</strong></div>
                  <div className="rounded border bg-green-50 p-3"><span className="text-xs text-gray-500">Entradas</span><strong className="mt-1 block text-green-700">R$ {reconciliation.cash.totals.inflow.toFixed(2).replace('.', ',')}</strong></div>
                  <div className="rounded border bg-red-50 p-3"><span className="text-xs text-gray-500">Saídas</span><strong className="mt-1 block text-red-700">R$ {reconciliation.cash.totals.outflow.toFixed(2).replace('.', ',')}</strong></div>
                  <div className="rounded border bg-blue-50 p-3"><span className="text-xs text-gray-500">Saldo final</span><strong className="mt-1 block text-blue-700">R$ {reconciliation.cash.totals.closingBalance.toFixed(2).replace('.', ',')}</strong></div>
                </div>
                <p className={`text-sm font-medium ${reconciliation.cashDifference === 0 ? 'text-green-700' : 'text-amber-700'}`}>
                  {reconciliation.cashDifference === 0
                    ? 'Consistência do caixa confirmada: saldo inicial + entradas − saídas = saldo final.'
                    : `Diferença detectada na equação do caixa: R$ ${reconciliation.cashDifference.toFixed(2).replace('.', ',')}.`}
                </p>
                <p className="text-xs text-gray-600">Este recorte valida a leitura atual e a consistência matemática do caixa. A comparação histórica do tenant foi aprovada no preflight 28b.</p>
              </div>}
            </div>
          </>
        ) : null}
      </section>

      <section className="rounded-lg border border-blue-200 bg-blue-50 p-5 text-sm text-blue-900">
        <strong>Ativação protegida.</strong> A gravação controlada é restrita ao tenant piloto. Backfill, migração e desativação do legado exigem etapas separadas e novo aceite.
      </section>
    </div>
  );
}
